import { useAppStore } from '@runtime/core/store/appStore'

import { ThemeEngine } from '@runtime/core/theme/themeEngine'

let started = false

/**
 * Keep the DOM theme (CSS vars + global assets) in sync with the persisted app store.
 *
 * Why this exists:
 * - React effects can run before/after Zustand rehydration depending on timing.
 * - We want the selected POSTER/theme to be applied reliably on page reload.
 */
export function startThemeSync(
  options: { shouldRestore?: () => boolean } = {},
) {
  if (started) return
  started = true

  const applyFromStore = () => {
    if (options.shouldRestore && !options.shouldRestore()) return
    try {
      if (options.shouldRestore) void ThemeEngine.restore(options.shouldRestore)
      else void ThemeEngine.restore()
    } catch {
      // ignore
    }
  }

  // Apply immediately (will use the initial store state).
  applyFromStore()

  // Ensure we apply again once Zustand has finished rehydrating.
  try {
    const persistApi = (
      useAppStore as unknown as {
        persist?: { onFinishHydration?: (cb: () => void) => void }
      }
    ).persist
    if (persistApi && typeof persistApi.onFinishHydration === 'function') {
      persistApi.onFinishHydration(() => applyFromStore())
    }
  } catch {
    // ignore
  }

  // Re-apply when the selected theme id changes.
  try {
    useAppStore.subscribe((state, prev) => {
      if (!state.themeId || state.themeId === prev.themeId) return
      applyFromStore()
    })
  } catch {
    // ignore
  }
}
